export type EmailType = 'NEW' | 'REPLY' | 'FORWARD' | 'FOLLOWUP';

export interface Signature {
  id: string;
  name: string;
  content: string;
  isDefault?: boolean;
}

export interface SignatureInfo {
  id: string;
  content: string;
  startIndex: number;
  endIndex: number;
}

export class SignatureManager {
  // Standardized HTML comment markers
  private static readonly MARKERS = {
    START: '<!-- cowbell-signature-start',
    END: '<!-- cowbell-signature-end -->',
  } as const;

  /**
   * Creates a signature HTML block with proper markers
   */
  private static wrapSignature(
    signature: Signature,
    includeLeadingSpacing = true,
    marginTop = '8px'
  ): string {
    const leadingSpace = includeLeadingSpacing ? '<br>\n' : '';
    return `${leadingSpace}${SignatureManager.MARKERS.START}:${signature.id} -->
<div style="margin-top: ${marginTop};">
${signature.content}
</div>
${SignatureManager.MARKERS.END}`;
  }

  /**
   * Detects existing signature in email content
   * Returns signature info if found, null otherwise
   */
  static detectSignature(content: string): SignatureInfo | null {
    if (!content) return null;

    const startPattern = new RegExp(
      `${SignatureManager.MARKERS.START.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      )}:([^>]+)\\s*-->`,
      'i'
    );
    const endPattern = new RegExp(
      SignatureManager.MARKERS.END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      'i'
    );

    const startMatch = content.match(startPattern);
    const endMatch = content.match(endPattern);

    if (
      !startMatch ||
      !endMatch ||
      startMatch.index === undefined ||
      endMatch.index === undefined
    ) {
      return null;
    }

    let actualStartIndex = startMatch.index;
    const endIndex = endMatch.index + endMatch[0].length;
    const signatureId = startMatch[1].trim();

    // Check if there are <br> tags immediately before the signature that should be included
    const beforeSignature = content.substring(0, actualStartIndex);
    const leadingSpaceMatch = beforeSignature.match(
      /(<br[^>]*>\s*<br[^>]*>\s*)$/i
    );

    if (leadingSpaceMatch) {
      // Include the leading spacing as part of the signature
      actualStartIndex = actualStartIndex - leadingSpaceMatch[1].length;
    }

    const signatureContent = content.substring(actualStartIndex, endIndex);

    return {
      id: signatureId,
      content: signatureContent,
      startIndex: actualStartIndex,
      endIndex,
    };
  }

  /**
   * Removes existing signature from content
   */
  static removeSignature(content: string): string {
    if (!content) return content;

    const signatureInfo = SignatureManager.detectSignature(content);
    if (!signatureInfo) return content;

    return (
      content.substring(0, signatureInfo.startIndex) +
      content.substring(signatureInfo.endIndex)
    );
  }

  /**
   * Replaces existing signature with new signature in-place
   */
  static replaceSignature(
    content: string,
    newSignature: Signature,
    emailType: EmailType
  ): string {
    const signatureInfo = SignatureManager.detectSignature(content);
    if (!signatureInfo) {
      return SignatureManager.insertSignature(content, newSignature, emailType);
    }

    // Get the content before and after signature, ensuring we preserve user content
    const beforeSignature = content.substring(0, signatureInfo.startIndex);
    const afterSignature = content.substring(signatureInfo.endIndex);

    // Replace the existing signature (spacing is already handled by detectSignature)
    const isReply = emailType === 'REPLY';
    const newSignatureHtml = SignatureManager.wrapSignature(
      newSignature,
      !isReply, // No leading spacing for replies
      isReply ? '4px' : '8px'
    );

    // Ensure proper spacing around signature to prevent content capture
    const result = beforeSignature + newSignatureHtml + afterSignature;

    return result;
  }

  /**
   * Inserts signature into email content based on email type
   */
  static insertSignature(
    content: string,
    signature: Signature,
    emailType: EmailType
  ): string {
    const cleanContent = content.trim();
    const isReply = emailType === 'REPLY';
    const signatureHtml = SignatureManager.wrapSignature(
      signature,
      !isReply, // No leading spacing for replies
      isReply ? '4px' : '8px'
    );

    switch (emailType) {
      case 'REPLY':
        return SignatureManager.insertReplySignature(
          cleanContent,
          signatureHtml
        );

      case 'FORWARD':
        return SignatureManager.insertForwardSignature(
          cleanContent,
          signatureHtml
        );

      case 'NEW':
      case 'FOLLOWUP':
      default:
        return SignatureManager.insertNewEmailSignature(
          cleanContent,
          signatureHtml
        );
    }
  }

  /**
   * Insert signature for new emails - simply append at the end
   */
  private static insertNewEmailSignature(
    content: string,
    signatureHtml: string
  ): string {
    const trimmedContent = content.trim();
    // Ensure there's always space for user to type above signature
    return trimmedContent + (trimmedContent ? '<br><br>' : '') + signatureHtml;
  }

  /**
   * Insert signature for replies - before quoted content
   */
  private static insertReplySignature(
    content: string,
    signatureHtml: string
  ): string {
    // More robust approach: Find the quoted div by its distinctive styling
    // This div always has: margin-left: 1em; border-left: 1px solid; padding-left: 1em
    const quotedDivIndex = content.indexOf(
      '<div style="margin-left: 1em; border-left: 1px solid'
    );

    if (quotedDivIndex !== -1) {
      // Look backwards from the div to find where the user content ends
      // The pattern should be: [user content]<br><br>On ... wrote:<br><div...
      const beforeDiv = content.substring(0, quotedDivIndex);
      const afterDiv = content.substring(quotedDivIndex);

      // Look for the "On ... wrote:" line that comes just before the div
      const wroteMatch = beforeDiv.match(
        /([\s\S]*?)(<br[^>]*>\s*<br[^>]*>\s*On .+? wrote:<br[^>]*>\s*)$/i
      );

      if (wroteMatch) {
        const userContent = wroteMatch[1].trim();
        const wroteHeader = wroteMatch[2];
        // Add empty lines at the top for the user to type if there's no user content
        const leadingSpace = userContent ? '' : '<br><br>';
        return (
          leadingSpace + userContent + signatureHtml + wroteHeader + afterDiv
        );
      } else {
        // No "wrote" pattern found, just insert signature before the quoted div
        const userContent = beforeDiv.trim();
        // Add empty lines at the top for the user to type if there's no user content
        const leadingSpace = userContent ? '' : '<br><br>';
        return leadingSpace + userContent + signatureHtml + afterDiv;
      }
    }

    // Fallback: append at end if no quoted content pattern found
    return SignatureManager.insertNewEmailSignature(content, signatureHtml);
  }

  /**
   * Insert signature for forwards - before forwarded message
   */
  private static insertForwardSignature(
    content: string,
    signatureHtml: string
  ): string {
    // For forwards, we want: [user content] + [signature] + [On...wrote:] + [quoted content]
    // Find the quoted div by its distinctive styling
    const quotedDivIndex = content.indexOf(
      '<div style="margin-left: 1em; border-left: 1px solid'
    );

    if (quotedDivIndex !== -1) {
      const beforeDiv = content.substring(0, quotedDivIndex);
      const afterDiv = content.substring(quotedDivIndex);

      // Look for "On ... wrote:" pattern that should go AFTER the signature
      // Handle cases where there are multiple <br> tags after "wrote:"
      const wroteMatch = beforeDiv.match(
        /([\s\S]*?)(<br[^>]*>\s*<br[^>]*>\s*On .+? wrote:(?:<br[^>]*>\s*)*)$/i
      );

      if (wroteMatch) {
        // Found "On...wrote:" - put signature BEFORE it
        const userContent = wroteMatch[1].trim();
        const wroteHeader = wroteMatch[2];
        return (
          userContent +
          (userContent ? '<br><br>' : '') +
          signatureHtml +
          wroteHeader +
          afterDiv
        );
      }

      // Look for traditional forward header pattern
      const forwardMatch = beforeDiv.match(
        /([\s\S]*?)(<br[^>]*>\s*<br[^>]*>\s*-{10}\s*Forwarded message\s*-{9,10}[\s\S]*)$/i
      );

      if (forwardMatch) {
        const userContent = forwardMatch[1].trim();
        const forwardHeader = forwardMatch[2];
        return (
          userContent +
          (userContent ? '<br><br>' : '') +
          signatureHtml +
          '<br>' +
          forwardHeader +
          afterDiv
        );
      }

      // No header pattern found, just insert signature before the quoted div
      const userContent = beforeDiv.trim();
      return (
        userContent +
        (userContent ? '<br><br>' : '') +
        signatureHtml +
        '<br>' +
        afterDiv
      );
    }

    // Fallback: append at end if no quoted content pattern found
    return SignatureManager.insertNewEmailSignature(content, signatureHtml);
  }

  /**
   * Checks if content has substantial user content (not just signature)
   */
  static hasSubstantialContent(content: string): boolean {
    if (!content) return false;

    // Remove any existing signature first
    const contentWithoutSignature = SignatureManager.removeSignature(content);
    const trimmedContent = contentWithoutSignature.trim();

    return (
      trimmedContent.length > 50 &&
      trimmedContent !== '<em>[No content available]</em>'
    );
  }

  /**
   * Determines if signature insertion is safe for the given content and email type
   */
  static isSafeToInsertSignature(
    content: string,
    emailType: EmailType
  ): boolean {
    switch (emailType) {
      case 'NEW':
      case 'REPLY':
      case 'FORWARD':
      case 'FOLLOWUP':
        // For all types, insertion is generally safe
        return true;

      default:
        return false;
    }
  }

  /**
   * Gets signature ID from content if one exists
   */
  static getSignatureId(content: string): string | null {
    const signatureInfo = SignatureManager.detectSignature(content);
    return signatureInfo?.id || null;
  }

  /**
   * Checks if content has a signature with the given ID
   */
  static hasSignatureWithId(content: string, signatureId: string): boolean {
    const existingId = SignatureManager.getSignatureId(content);
    return existingId === signatureId;
  }
}
